import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Loading, Modal, Text, ThumbnailButtonImageViewer } from '@tradingblock/components';
import _ from 'lodash';
import { ApplicationActions } from '../../state/actions';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import { Form, Formik, FormikProps } from 'formik';
import { ApiResponse, InvestigationDocuments, ResponseCodes } from '@tradingblock/types';
import { useApi, useDocumentStore, useFileStore } from '../../context';
import { useDropzone } from 'react-dropzone';
import { PDFDocument } from 'pdf-lib';
import { AppOrInv, DocumentToUpload, MongoDocumentToUpload, MongoFileToUpload } from '../../types';
import imageCompression from 'browser-image-compression';

const compress = async (file: File) => {
  const compressed = await imageCompression(file, {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1024,
    useWebWorker: true,
  });
  return compressed;
};

interface CameraFormValues {
  selectedDocument: InvestigationDocuments;
}

interface DocumentUploadProps {
  isRnWebView: boolean;
  dispatch: any;
  accountId: number | undefined;
  participant: any;
  disabled: boolean;
  setDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  areDocumentsRequested: boolean;
  primary: boolean;
}

interface CameraFormValues {
  selectedDocument: InvestigationDocuments;
}

export default function DocumentUpload({
  isRnWebView,
  dispatch,
  disabled,
  setDisabled,
  accountId,
  participant,
  areDocumentsRequested,
  primary,
}: DocumentUploadProps) {
  const device = isRnWebView ? 'Camera' : 'Webcam';
  const api = useApi();
  const docStore = useDocumentStore();
  const fileStore = useFileStore();
  const [loading, setLoading] = useState<boolean>(false);
  const [reloadingDocs, setReloadingDocs] = useState<boolean>(false);
  const [tempBlob, setTempBlob] = useState<Blob | null>(null);
  const [tempPhotoSide, setTempPhotoSide] = useState<boolean | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const photoRef = useRef<HTMLCanvasElement | null>(null);
  const frontImageInputRef = useRef<HTMLInputElement | null>(null);
  const backImageInputRef = useRef<HTMLInputElement | null>(null);
  const backImageInput2Ref = useRef<HTMLInputElement | null>(null);
  const frontImageInput2Ref = useRef<HTMLInputElement | null>(null);
  const frontImageInput3Ref = useRef<HTMLInputElement | null>(null);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cameraLoaded, setCameraLoaded] = useState(false);
  const [cameraErrored, setCameraErrored] = useState(false);
  const [cameraExists, setCameraExists] = useState(false);
  const [cameraPermission, setCameraPermission] = useState(true);
  const [openCameraModal, setOpenCameraModal] = useState(false);
  const [errorState, setErrorState] = useState<string>('');
  const [loadingError, setLoadingError] = useState<string>('');
  const [side, setSide] = useState<string>('');
  const [excludeName, setExcludeName] = useState<string[]>([]);
  const [appInvList, setAppInvList] = useState<AppOrInv[]>([]);
  const [requestedDocuments, setRequestedDocuments] = useState<InvestigationDocuments[]>([]);
  const [queuedDocuments, setQueuedDocuments] = useState<DocumentToUpload[]>([]);
  const [selectedDocumentName, setSelectedDocumentName] = useState<InvestigationDocuments>();
  const [selectedDocumentSectionList, setSelectedDocumentSectionList] = useState<
    { id: number; type: string; docs: InvestigationDocuments[]; name: string }[]
  >([]);
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);

  const areDocumentRequirementsFullfilled = useMemo(() => {
    return appInvList.length === 0 && queuedDocuments.length > 0;
  }, [appInvList]);

  //#region Section for utilizing media devicees to upload for required documents
  const formatDocumentName = (documentName: string) => {
    if (!documentName) return '';

    const specialCases: { [key: string]: string } = {
      DriversLicense: "Driver's License",
      StateIdCard: 'State ID Card',
      Passport: 'Passport',
      MilitaryId: 'Military ID',
      SsnCard: 'SSN Card',
      SsaLetter: 'SSA Letter',
      IrsItinLetter: 'IRS ITIN Letter',
      OtherGovernmentId: 'Other Government ID',
      CddDocument: 'CDD Document',
      AllPassingCipResults: 'All Passing CIP Results',
      IrsW9Form: 'IRS W-9 Form',
      SignatureImage: 'Signature Image',
      IdDocument: 'ID Document',
      AffiliateApproval: 'Affiliate Approval',
      Document: 'Document',
      Other: 'Other',
      GovernmentId: 'Government ID',
    };

    if (specialCases[documentName]) {
      return specialCases[documentName];
    }

    return (
      documentName
        // Add spaces before each capital letter except the first one
        .replace(/([A-Z])/g, ' $1')
        // Trim any leading spaces
        .trim()
        // Convert to title case
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
    );
  };

  const tryGetDocument = async (key: string) => {
    try {
      const res = (await docStore.get<MongoDocumentToUpload>('document/' + key)) as MongoDocumentToUpload;
      if (res && res.key === key) {
        return res.document;
      }

      return undefined;
    } catch (err) {
      console.warn('storage get error ', err);
      return Promise.resolve(undefined);
    }
  };

  const tryGetFrontFile = async (key: string) => {
    try {
      const res = (await fileStore.get<MongoFileToUpload>('frontFile/' + key)) as MongoFileToUpload;
      if (res && res.key === key) {
        return base64ToFile(res.file, res.fileName);
      }

      return undefined;
    } catch (err) {
      console.warn('storage get error ', err);
      return Promise.resolve(undefined);
    }
  };

  const tryGetBackFile = async (key: string) => {
    try {
      const res = (await fileStore.get<MongoFileToUpload>('backFile/' + key)) as MongoFileToUpload;
      if (res && res.key === key) {
        return base64ToFile(res.file, res.fileName);
      }

      return undefined;
    } catch (err) {
      console.warn('storage get error ', err);
      return Promise.resolve(undefined);
    }
  };

  const trySaveDocument = async (key: string, document: DocumentToUpload) => {
    try {
      const doc = { ...document } as DocumentToUpload;
      doc.frontFile = undefined;
      doc.backFile = undefined;
      await docStore.save<MongoDocumentToUpload>('document/' + key, {
        key: key,
        document: doc,
      });
    } catch (err) {
      console.warn('storage save error ', err, key, document);
      return Promise.resolve(false);
    }
  };

  const trySaveFrontFile = async (key: string, fileToUpload: File) => {
    try {
      const file = await convertFileToBase64(fileToUpload);
      await fileStore.save<MongoFileToUpload>('frontFile/' + key, {
        key: key,
        file: file as string,
        fileName: fileToUpload.name,
        fileType: fileToUpload.type,
      });
    } catch (err) {
      console.warn('storage save error ', err, key, document);
      return Promise.resolve(false);
    }
  };

  const trySaveBackFile = async (key: string, fileToUpload: File) => {
    try {
      const file = await convertFileToBase64(fileToUpload);
      await fileStore.save<MongoFileToUpload>('backFile/' + key, {
        key: key,
        file: file as string,
        fileName: fileToUpload.name,
        fileType: fileToUpload.type,
      });
    } catch (err) {
      console.warn('storage save error ', err, key, document);
      return Promise.resolve(false);
    }
  };

  const tryDeleteFrontFile = async (key: string) => {
    try {
      await fileStore.delete('frontFile/' + key);
    } catch (err) {
      console.warn('storage get error ', err);
      return Promise.resolve(undefined);
    }
  };

  const tryDeleteBackFile = async (key: string) => {
    try {
      await fileStore.delete('backFile/' + key);
    } catch (err) {
      console.warn('storage get error ', err);
      return Promise.resolve(undefined);
    }
  };

  const tryDeleteDoc = async (key: string) => {
    try {
      await fileStore.delete('document/' + key);
    } catch (err) {
      console.warn('storage get error ', err);
      return Promise.resolve(undefined);
    }
  };

  const base64ToFile = (base64: string, fileName: string): File => {
    const arr = base64.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';

    const byteString = atob(arr[1]); // Decode Base64
    const byteNumbers = new Uint8Array(byteString.length);

    for (let i = 0; i < byteString.length; i++) {
      byteNumbers[i] = byteString.charCodeAt(i);
    }

    const file = new File([byteNumbers], fileName, { type: mimeType });
    return file;
  };

  const convertFileToBase64 = (file: File) => {
    if (!file) return '';
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const getFromMongoDB = async (key: string) => {
    const promiseArray: any[] = [];

    promiseArray.push(tryGetDocument(key));
    promiseArray.push(tryGetFrontFile(key + '-front'));
    promiseArray.push(tryGetBackFile(key + '-back'));

    const results = await Promise.allSettled(promiseArray);

    const values = results
      .filter(result => result.status === 'fulfilled' && result.value !== undefined)
      .map(result => (result as PromiseFulfilledResult<DocumentToUpload | File>).value);

    let doc = values.find(val => typeof val === 'object' && typeof val.isUpdating === 'boolean');
    if (doc && values.length > 0) {
      doc.frontFile = values[1] as File;
      if (values.length > 2) doc.backFile = values[2] as File;
      return doc;
    }

    return null;
  };

  const saveToMongoDB = async (key: string, data: DocumentToUpload) => {
    const results = await Promise.allSettled([
      trySaveDocument(key, data),
      data.frontFile && data.frontFile !== undefined ? trySaveFrontFile(key + '-front', data.frontFile) : null,
      data.backFile && data.backFile !== undefined ? trySaveBackFile(key + '-back', data.backFile) : null,
    ]);
  };

  const deleteFromMongoDB = async (key: string) => {
    const results = await Promise.allSettled([
      tryDeleteFrontFile(key + '-front'),
      tryDeleteBackFile(key + '-back'),
      tryDeleteDoc(key),
    ]);
  };

  const storeDocumentToMongo = async (document: DocumentToUpload) => {
    saveToMongoDB(accountId + '-' + (primary ? '0' : '1') + '-' + document.selectedDocument, document);
  };

  const handleRemoveMongoDoc = (doc: string) => {
    deleteFromMongoDB(accountId + '-' + (primary ? '0' : '1') + '-' + doc);
  };

  useEffect(() => {
    setDisabled(uploading);
  }, [uploading]);

  const timeoutMs = 10000;
  function withTimeout<T>(promise: Promise<T>): Promise<T> {
    const timeoutPromise = new Promise<T>((_, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('Operation timed out'));
      }, timeoutMs);

      promise.finally(() => clearTimeout(timer)); // Clear the timer when the promise settles
    });

    return Promise.race([promise, timeoutPromise]);
  }

  useEffect(() => {
    if (!openCameraModal) stopVideo();
  }, [openCameraModal]);

  // if a user has selected a document that requires a front and back image, and they have not uploaded both images, but they change the selected document, we should clear the images
  useEffect(() => {
    if (isUpdating) {
      const fakeFile = new File([], 'none');
      if (frontImage || backImage)
        updateQueuedDocument(
          side === 'front' ? (frontImage ? frontImage : fakeFile) : backImage ? backImage : fakeFile,
          side,
          selectedDocumentName as string
        );

      setFrontImage(null);
      setBackImage(null);
    } else if (isIdCard) {
      if (frontImage && backImage) {
        const tempAppOrInvList = appInvList.filter(item => item.docs.includes(selectedDocumentName));
        if (tempAppOrInvList && tempAppOrInvList.length > 0) {
          const docToAdd = {
            satisfiedAppOrInv: tempAppOrInvList,
            frontFile: frontImage,
            backFile: backImage,
            selectedDocument: selectedDocumentName,
            isUpdating: false,
          } as DocumentToUpload;
          setQueuedDocuments(prev => [...prev, docToAdd]);
          storeDocumentToMongo(docToAdd);
          setFrontImage(null);
          setBackImage(null);
        }
      }
    } else if (frontImage) {
      const tempAppOrInvList = appInvList.filter(item =>
        item.docs.includes(selectedDocumentName as InvestigationDocuments)
      );
      if (tempAppOrInvList && tempAppOrInvList.length > 0) {
        const docToAdd = {
          satisfiedAppOrInv: tempAppOrInvList,
          frontFile: frontImage,
          backFile: undefined,
          selectedDocument: selectedDocumentName,
          isUpdating: false,
        } as DocumentToUpload;
        setQueuedDocuments(prev => [...prev, docToAdd]);
        storeDocumentToMongo(docToAdd);
        setFrontImage(null);
        setBackImage(null);
      }
    }
    setErrorState('');
  }, [frontImage, backImage]);

  const isIdCard =
    selectedDocumentName === InvestigationDocuments.StateIdCard ||
    selectedDocumentName === InvestigationDocuments.DriversLicense;

  // maxFileSize variable set to 10MB
  const maxFileSize = 7 * 1024 * 1024;

  async function createMergedPDF(files: File[]): Promise<File | undefined> {
    const pdfDoc = await PDFDocument.create();

    for (const file of files) {
      if (!file) continue;

      if (file.type === 'application/pdf') {
        // Load and copy pages from the existing PDF
        const existingPdfBytes = await file.arrayBuffer();
        const existingPdf = await PDFDocument.load(existingPdfBytes);
        const pages = await pdfDoc.copyPages(existingPdf, existingPdf.getPageIndices());
        pages.forEach(page => pdfDoc.addPage(page));
      } else if (file.type.match('image.*')) {
        // Load and embed the image
        const compressedFile = await compress(file);
        let imageData = await compressedFile.arrayBuffer();
        let image;

        if (file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png') {
          image = file.type.includes('png') ? await pdfDoc.embedPng(imageData) : await pdfDoc.embedJpg(imageData);
        } else if (file.type === 'image/gif') {
          // Convert GIF to JPEG before embedding
          imageData = await convertGifToJpeg(compressedFile);
          image = await pdfDoc.embedJpg(imageData);
        }

        if (image) {
          const page = pdfDoc.addPage();
          const scale = Math.min(page.getWidth() / image.width, page.getHeight() / image.height);
          page.drawImage(image, {
            x: 0,
            y: page.getHeight() - image.height * scale,
            width: image.width * scale,
            height: image.height * scale,
          });
        } else {
          console.error('Failed to convert file');
          continue; // Skip failed file
        }
      } else {
        console.warn('Unsupported file type.');
        continue; // Skip unsupported files
      }
    }

    const pdfBytes = await pdfDoc.save();
    return new File([pdfBytes], 'combined.pdf', { type: 'application/pdf' });
  }

  async function convertGifToJpeg(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, img.width, img.height);
          canvas.toBlob(blob => {
            if (blob) {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve(reader.result as ArrayBuffer);
              };
              reader.onerror = reject;
              reader.readAsArrayBuffer(blob);
            } else {
              reject(new Error('Could not convert GIF to JPEG'));
            }
          }, 'image/jpeg');
        } else {
          reject(new Error('Could not convert GIF to JPEG'));
        }
      };
      img.onerror = reject;
      const reader = new FileReader();
      reader.onload = e => {
        if (e.target) img.src = e.target.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const handleUpload = async (documentToUpload: DocumentToUpload) => {
    const appOrInv = documentToUpload.satisfiedAppOrInv.find(item =>
      item.docs.includes(documentToUpload.selectedDocument)
    );
    if (appOrInv) {
      try {
        const fileArray: File[] = [];
        if (documentToUpload.frontFile) fileArray.push(documentToUpload.frontFile);
        if (documentToUpload.backFile) fileArray.push(documentToUpload.backFile);
        const fileToUpload = await createMergedPDF(fileArray);
        if (fileToUpload) {
          handleRemoveMongoDoc(documentToUpload.selectedDocument);
          if (appOrInv.type === 'app' && appOrInv.id !== 0 && accountId) {
            setOpenCameraModal(false);
            setUploading(true);
            await withTimeout(
              api.application
                .uploadApplicationDocument(fileToUpload, accountId, appOrInv.id, documentToUpload.selectedDocument)
                .then((response: ApiResponse<number>) => {
                  processUploadResponse(response);
                })
            );
          } else if (appOrInv.type === 'inv' && appOrInv.id !== 0 && accountId) {
            setOpenCameraModal(false);
            setUploading(true);
            await withTimeout(
              api.application
                .uploadInvestigationDocument(fileToUpload, accountId, appOrInv.id, documentToUpload.selectedDocument)
                .then((response: ApiResponse<number>) => {
                  processUploadResponse(response);
                })
            );
          }
        } else {
          throw new Error('File to upload is undefined.');
        }
      } catch (error) {
        setUploading(false);
        setErrorState('An error occurred while processing the upload.');
      }
    } else {
      setErrorState('Can not find matching document');
    }
  };

  const handleUploadAll = () => {
    queuedDocuments.forEach(qd => handleUpload(qd));
  };

  const fetchStatus = (accountId: number) => {
    setSuccess(false);
    setRequestedDocuments([]);
    setAppInvList([]);
    checkForCamera();
    dispatch(ApplicationActions.requestApplicationStatus({ accountId }));
  };

  //Processes the upload response from the api
  const processUploadResponse = (response: ApiResponse<number>) => {
    setUploading(false);
    // check if the upload was not successful and set the errors/initial states.
    if (response.responseCode !== 0) {
      setErrorState(ResponseCodes[response.responseCode].description as string);
    } else {
      setSuccess(true);
      // There were no errors, reset the states and set success to true
      setErrorState('');
      const fetchStatusTimer = setTimeout(fetchStatus, 1500, accountId);
      return () => clearTimeout(fetchStatusTimer);
    }
  };

  // Function to check for available cameras
  const checkForCamera = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasVideoInput = devices.some(device => device.kind === 'videoinput');
        setCameraExists(hasVideoInput); // Update state based on presence of camera
      }
    } catch (error) {
      console.error('Error checking for camera:', error);
    }
  };

  async function checkCameraPermission() {
    try {
      const result = await navigator.permissions.query({ name: 'camera' as PermissionName });

      if (result.state === 'granted') {
        setCameraExists(true);
      } else if (result.state === 'denied') {
        setCameraExists(false);
        setCameraPermission(false);
      }

      // Listen for changes in permission state
      result.onchange = () => {
        if (result.state === 'denied') {
          setCameraExists(false);
          setCameraPermission(false);
        }
      };
    } catch (error) {
      console.log('Error checking camera permission:', error);
    }
  }

  useEffect(() => {
    checkForCamera();
    checkCameraPermission();
    setReloadingDocs(true);
  }, []); // Runs once on component mount

  useEffect(() => {
    if (reloadingDocs && requestedDocuments && requestedDocuments.length > 0) {
      setReloadingDocs(false);
      checkForMongoDocs(requestedDocuments);
    }
  }, [requestedDocuments, reloadingDocs]);

  const checkForMongoDocs = async (requestedDocuments: InvestigationDocuments[]) => {
    setLoading(true);
    const getDocPromises = requestedDocuments.map(name =>
      getFromMongoDB(accountId + '-' + (primary ? '0' : '1') + '-' + name).then(doc => doc || null)
    );
    const results = await Promise.allSettled(getDocPromises);

    const docs = results
      .filter(result => result.status === 'fulfilled' && result.value !== null)
      .map(result => (result as PromiseFulfilledResult<DocumentToUpload>).value);

    setQueuedDocuments(docs);
    setLoading(false);
  };

  const currentTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (cameraLoaded) {
      clearTimeout(currentTimeout.current as NodeJS.Timeout);
    }
  }, [cameraLoaded, currentTimeout]);

  const getVideo = useCallback(
    async (counter: number = 3) => {
      try {
        checkCameraPermission();
        setCameraLoaded(false);
        setLoadingError('');

        streamRef.current = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 },
            facingMode: 'environment',
          },
        });

        let video = videoRef.current;
        if (video && streamRef.current) {
          video.srcObject = streamRef.current;
          video.setAttribute('playsinline', 'true');
          video.setAttribute('webkit-playsinline', 'true');
          video.play();
          video.onloadeddata = () => {
            setCameraLoaded(true);
          };

          if (counter > 0) {
            currentTimeout.current = setTimeout(() => {
              console.log('Retrying media capture. Try Count: ', counter);
              getVideo(counter - 1);
              currentTimeout.current = null;
            }, 3000);
            return () => clearTimeout(currentTimeout.current as NodeJS.Timeout);
          } else {
            setCameraErrored(true);
            setCameraExists(false);
          }
        }
      } catch (err) {
        console.error(err);

        if ((err as Error).name === 'NotReadableError') {
          setLoadingError(
            `${device} is already in use. To upload with ${device}, please reset the ${device} and try again.`
          );
        } else if (
          (counter > 0 && !((err as Error).name === 'NotAllowedError')) ||
          (err as Error).name === 'PermissionDeniedError'
        ) {
          currentTimeout.current = setTimeout(() => {
            console.log('Retrying media capture. Try Count: ', counter);
            getVideo(counter - 1);
            currentTimeout.current = null;
          }, 3000);
          return () => clearTimeout(currentTimeout.current as NodeJS.Timeout);
        } else {
          setCameraErrored(true);
          setCameraExists(false);
        }
      }
    },
    [cameraPermission]
  );

  function stopVideo() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  }
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDimensions({
        width: videoRef.current.videoWidth,
        height: videoRef.current.videoHeight,
      });
    }
  };

  const takePhoto = (backPhoto = false) => {
    const width = dimensions.width;
    const height = dimensions.height;
    let video = videoRef.current;
    let photo = photoRef.current;

    if (video && photo) {
      photo.width = width;
      photo.height = height;

      let context = photo.getContext('2d');
      if (context) {
        // Enable smooth scaling
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        context.drawImage(video, 0, 0);
        setHasPhoto(true);

        photo.toBlob(blob => {
          if (blob) {
            setTempPhotoSide(backPhoto);
            setTempBlob(blob);
          }
        }, 'image/jpeg');

        setOpenCameraModal(false);
      }
    }
  };

  useEffect(() => {
    if (tempBlob !== null && tempPhotoSide !== null) {
      const imageName = isIdCard
        ? !tempPhotoSide
          ? 'front-' + selectedDocumentName + '.jpg'
          : 'back-' + selectedDocumentName + '.jpg'
        : selectedDocumentName + '.jpg';

      setSide(tempPhotoSide ? 'back' : 'front');

      // Create a File object from the Blob
      if (!tempPhotoSide) handleImageDrop('front')([new File([tempBlob], imageName, { type: 'image/jpeg' })]);
      else handleImageDrop('back')([new File([tempBlob], imageName, { type: 'image/jpeg' })]);
      setHasPhoto(false);
      setTempBlob(null);
      setTempPhotoSide(null);
    }
  }, [tempPhotoSide, tempBlob]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    const backPhoto = event.target.id !== 'frontImage';

    if (file) {
      setSide(backPhoto ? 'back' : 'front');
      const imageName = isIdCard
        ? !backPhoto
          ? 'front-' + selectedDocumentName + '.jpg'
          : 'back-' + selectedDocumentName + '.jpg'
        : selectedDocumentName + '.jpg';
      const newFile = new File([file], imageName, { type: 'image/jpeg' });

      // Create a File object from the Blob
      if (!backPhoto) setFrontImage(newFile);
      else setBackImage(newFile);
      setHasPhoto(false);
    }
  };

  const handleFileUpdate = (event: React.ChangeEvent<HTMLInputElement>, section: DocumentToUpload) => {
    const file = event.target.files && event.target.files[0];
    const backPhoto = event.target.id !== 'frontImage';

    if (file) {
      const imageName = isIdCard
        ? !backPhoto
          ? 'front-' + section.selectedDocument + '.jpg'
          : 'back-' + section.selectedDocument + '.jpg'
        : section.selectedDocument + '.jpg';
      const newFile = new File([file], imageName, { type: 'image/jpeg' });
      updateQueuedDocument(newFile, !backPhoto ? 'front' : 'back', section.selectedDocument);
      setHasPhoto(false);
    }
  };

  const updateQueuedDocument = (file: File, side: string, selDoc: string) => {
    const docToChange = queuedDocuments.map(item => {
      if (item.selectedDocument === selDoc) {
        let tempItem: DocumentToUpload;
        if (side === 'front') tempItem = { ...item, frontFile: file, isUpdating: false };
        else tempItem = { ...item, backFile: file, isUpdating: false };
        storeDocumentToMongo(tempItem);
        return tempItem;
      } else return item;
    });
    setQueuedDocuments(docToChange);
  };
  //#endregion

  useEffect(() => {
    let tempAppOrInvList: AppOrInv[] = [];
    queuedDocuments.forEach(doc => {
      if (doc.satisfiedAppOrInv) tempAppOrInvList = [...tempAppOrInvList, ...doc.satisfiedAppOrInv];
    });
    setExcludeName(tempAppOrInvList.map(temp => temp.name));
    if (!isUpdating) setSelectedDocumentName(undefined);
  }, [queuedDocuments]);

  useEffect(() => {
    let letRequestedDocuments: InvestigationDocuments[] = [];
    let letAppInvList: AppOrInv[] = [];
    // let letExcludedDocuments: InvestigationDocuments[] = [];
    // let letExcludedAppInvList: AppOrInv[] = [];
    if (participant.requestedCatagories.application.length > 0) {
      participant.requestedCatagories.application.map(application => {
        if (!excludeName.includes(application.name)) {
          letRequestedDocuments = [...new Set([...letRequestedDocuments, ...application.requestedDocuments])];
          letAppInvList = [
            ...new Set([
              ...letAppInvList,
              {
                id: participant.accountHolderId ? participant.accountHolderId : 0,
                type: 'app',
                docs: application.requestedDocuments,
                name: application.name,
              },
            ]),
          ];
        }
      });
    }

    if (participant.requestedCatagories.investigations.length > 0) {
      participant.requestedCatagories.investigations.map(investigation => {
        if (!excludeName.includes(investigation.name)) {
          letRequestedDocuments = [...new Set([...letRequestedDocuments, ...investigation.requestedDocuments])];
          letAppInvList = [
            ...new Set([
              ...letAppInvList,
              {
                id: participant.investigationId ? participant.investigationId : 0,
                type: 'inv',
                docs: investigation.requestedDocuments,
                name: investigation.name,
              },
            ]),
          ];
        }
      });
    }
    setRequestedDocuments(letRequestedDocuments);
    setAppInvList(letAppInvList);
    if (!selectedDocumentName) {
      setSelectedDocumentName(letRequestedDocuments[0]);
    }
    setSelectedDocumentSectionList(
      letAppInvList.filter(item =>
        item.docs.includes(selectedDocumentName === undefined ? letRequestedDocuments[0] : selectedDocumentName)
      )
    );
    setErrorState('');
  }, [participant, selectedDocumentName, excludeName]);

  const { getRootProps: getFrontRootProps, getInputProps: getFrontInputProps } = useDropzone({
    onDrop: acceptedFiles => handleImageDrop('front')(acceptedFiles),
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: 'image/png, image/jpeg, image/jpg, image/gif, application/pdf, capture=camera',
    onDropRejected: fileRejections => {
      //Set the upload failed and the file errors to display
      setFrontImage(null);
      setErrorState('Error: The uploaded file is greater than 7MB');
    },
  });

  const { getRootProps: getBackRootProps, getInputProps: getBackInputProps } = useDropzone({
    onDrop: acceptedFiles => handleImageDrop('back')(acceptedFiles),
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: 'image/png, image/jpeg, image/jpg, image/gif, application/pdf, capture=camera',
    onDropRejected: fileRejections => {
      //Set the upload failed and the file errors to display
      setBackImage(null);
      setErrorState('Error: The uploaded file is greater than 7MB');
    },
  });

  const handleImageDrop = (side: 'front' | 'back') => async (acceptedFiles: File[]) => {
    // if the side is front, set the front image state
    // if the side is back, set the back image state
    const file = acceptedFiles[0];
    // if both the front and back images are set, combine them into a single PDF and upload automatically without user intervention
    setSide(side);
    if (side === 'front') setFrontImage(file);
    else setBackImage(file);
  };

  const handleRemoveQueuedDocument = (docToRemove: string) => {
    setQueuedDocuments(queuedDocuments.filter(qd => qd.selectedDocument !== docToRemove));
    handleRemoveMongoDoc(docToRemove);
  };

  const handleImageDelete = (side: string, selDoc: string) => {
    const docToChange = queuedDocuments.map(item => {
      if (item.selectedDocument === selDoc) {
        if (side === 'front') return { ...item, frontFile: undefined, isUpdating: true };
        else return { ...item, backFile: undefined, isUpdating: true };
      } else return item;
    });

    setQueuedDocuments(docToChange);
    setSelectedDocumentName(selDoc as InvestigationDocuments);
  };

  const isUpdating = useMemo(() => {
    return queuedDocuments.filter(qd => qd.isUpdating).length > 0;
  }, [queuedDocuments]);

  const sectionsForSelectedDocument = useMemo(() => {
    let includedSections: string[] = [];
    let excludedSections: string[] = [];
    _.uniqBy(appInvList, 'name').map(section => {
      const foundSection = selectedDocumentSectionList.find(doc => doc.name === section.name);
      if (foundSection) includedSections.push(formatDocumentName(section.name));
      else excludedSections.push(formatDocumentName(section.name));
    });
    return { included: includedSections, excluded: excludedSections };
  }, [selectedDocumentName, selectedDocumentSectionList, appInvList]);

  return (
    <>
      {!loading && (
        <div className="mb-4" style={isUpdating ? { cursor: 'not-allowed' } : {}}>
          <div style={isUpdating ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
            {selectedDocumentName && (
              <>
                <span>The following information will be verified with the requested document(s):</span>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                  }}
                >
                  <ul
                    className="mt-3"
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      textAlign: 'left',
                    }}
                  >
                    {sectionsForSelectedDocument.included.map((section, index) => {
                      return (
                        <li className="item" key={index}>
                          <strong>
                            <i className="fas fa-check-circle text-success" /> Proof of {section}
                          </strong>
                        </li>
                      );
                    })}
                  </ul>
                  <ul
                    className="mt-3"
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      textAlign: 'left',
                    }}
                  >
                    {sectionsForSelectedDocument.excluded.map((section, index) => {
                      return (
                        <li className="item" key={index}>
                          <strong>
                            <i className="fas fa-check-circle text-danger" /> Proof of {section}
                          </strong>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </>
            )}
            {requestedDocuments && requestedDocuments.length > 0 && (
              <ul className={'checklist my-4'}>
                {requestedDocuments.map(requestedDocumentOption => (
                  <li
                    key={`${requestedDocumentOption}_${
                      participant.investigationId ? participant.investigationId : participant.accountHolderId
                    }`}
                  >
                    <label>
                      <span className={'checkcontainer'}>
                        <input
                          type={'checkbox'}
                          name={`${requestedDocumentOption}_${
                            participant.investigationId ? participant.investigationId : participant.accountHolderId
                          }`}
                          id={`${requestedDocumentOption}_${
                            participant.investigationId ? participant.investigationId : participant.accountHolderId
                          }`}
                          value={requestedDocumentOption}
                          checked={requestedDocumentOption === selectedDocumentName}
                          onChange={() => {
                            setSelectedDocumentName(requestedDocumentOption);
                            setFrontImage(null);
                            setBackImage(null);
                          }}
                        />
                        <span className={'radiobtn'}></span>
                      </span>
                      <Text id={`investigationUpload.documentOptions.${requestedDocumentOption}`} type={'field'} />
                    </label>
                  </li>
                ))}
              </ul>
            )}
            {selectedDocumentName && (
              <>
                {!isIdCard && (
                  <div
                    className=" mt-2"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div {...getFrontRootProps()}>
                      <input {...getFrontInputProps()} />
                      <Button className={'btn-btn btn-primary'} disabled={disabled}>
                        <Text id={'investigationUpload.selectUpload'} type={'field'} />
                        <Text
                          id={'investigationUpload.upload'}
                          type={'field'}
                          data={{
                            documentName: formatDocumentName(selectedDocumentName as string),
                          }}
                        />
                      </Button>
                    </div>
                    {cameraPermission &&
                      !cameraErrored &&
                      (cameraExists || (!cameraExists && isRnWebView)) &&
                      areDocumentsRequested && (
                        <>
                          <input
                            type="file"
                            id="frontImage"
                            ref={frontImageInput2Ref}
                            onChange={handleFileChange}
                            capture="environment"
                            accept="image/*"
                            hidden
                          />
                          <button
                            onClick={() => {
                              if (isRnWebView) frontImageInput2Ref.current && frontImageInput2Ref.current.click();
                              else {
                                setOpenCameraModal(!openCameraModal);
                                setSide('front');
                                if (!openCameraModal && !isRnWebView) getVideo();
                              }
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                            }}
                          >
                            <i
                              className="fas fa-camera"
                              style={{ fontSize: '36px' }}
                              title="Take photo with your camera."
                            />
                          </button>
                        </>
                      )}
                  </div>
                )}

                {isIdCard && requestedDocuments && requestedDocuments.length > 0 && (
                  <>
                    {/* Only display if there is no frontImage state currently set*/}
                    {frontImage === null && (
                      <div
                        className=" mt-2"
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div {...getFrontRootProps()}>
                          <input {...getFrontInputProps()} />
                          <Button className={'btn-btn btn-primary mr-2'} disabled={disabled}>
                            <Text id={'investigationUpload.selectUpload'} type={'field'} />
                            <Text
                              id={'investigationUpload.upload'}
                              type={'field'}
                              data={{
                                documentName: formatDocumentName(selectedDocumentName) + ' - Front',
                              }}
                            />
                          </Button>
                        </div>
                        {cameraPermission &&
                          !cameraErrored &&
                          (cameraExists || (!cameraExists && isRnWebView)) &&
                          areDocumentsRequested && (
                            <>
                              <input
                                type="file"
                                id="frontImage"
                                ref={frontImageInputRef}
                                onChange={handleFileChange}
                                capture="environment"
                                accept="image/*"
                                hidden
                              />
                              <button
                                onClick={() => {
                                  if (isRnWebView) frontImageInputRef.current && frontImageInputRef.current.click();
                                  else {
                                    setOpenCameraModal(!openCameraModal);
                                    setSide('front');
                                    if (!openCameraModal && !isRnWebView) getVideo();
                                  }
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                }}
                              >
                                <i
                                  className="fas fa-camera"
                                  style={{ fontSize: '36px' }}
                                  title="Take photo with your camera."
                                />
                              </button>
                            </>
                          )}
                      </div>
                    )}
                    {/* Only display if there is no backImage state currently set*/}
                    {backImage === null && (
                      <div
                        className=" mt-2"
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div {...getBackRootProps()}>
                          <input {...getBackInputProps()} />
                          <Button className={'btn-btn btn-primary mr-2'} disabled={disabled}>
                            <Text id={'investigationUpload.selectUpload'} type={'field'} />
                            <Text
                              id={'investigationUpload.upload'}
                              type={'field'}
                              data={{
                                documentName: formatDocumentName(selectedDocumentName) + ' - Back',
                              }}
                            />
                          </Button>
                        </div>
                        {cameraPermission &&
                          !cameraErrored &&
                          (cameraExists || (!cameraExists && isRnWebView)) &&
                          areDocumentsRequested && (
                            <>
                              <input
                                type="file"
                                id="frontImage"
                                ref={backImageInputRef}
                                onChange={handleFileChange}
                                capture="environment"
                                accept="image/*"
                                hidden
                              />
                              <button
                                onClick={() => {
                                  if (isRnWebView) backImageInputRef.current && backImageInputRef.current.click();
                                  else {
                                    setOpenCameraModal(!openCameraModal);
                                    setSide('back');
                                    if (!openCameraModal) getVideo();
                                  }
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                }}
                              >
                                <i
                                  className="fas fa-camera"
                                  style={{ fontSize: '36px' }}
                                  title="Take photo with your camera."
                                />
                              </button>
                            </>
                          )}
                      </div>
                    )}
                  </>
                )}
                <br />
                <span className="mute" style={{ marginTop: '1rem' }}>
                  <Text id={'review-documents.files'} page="account" />
                </span>
              </>
            )}

            {success && (
              <div className=" mb-4">
                <i className="fas fa-check-circle text-success" />{' '}
                <Text id={'investigationUpload.uploadSuccessful'} type={'field'} />
              </div>
            )}
            {uploading && (
              <div className=" mb-4">
                <i className="fas fa-circle-notch fa-spin valid" />{' '}
                <Text id={'investigationUpload.uploading'} type={'field'} />
              </div>
            )}
            {errorState && !isUpdating && <div className="error mb-4">{errorState}</div>}

            {!cameraPermission && !isRnWebView && (
              <div className="mb-4">
                <strong>
                  {device} Permission has been denied. To upload with {device}, please reset/allow the perimission and
                  refresh the page.
                </strong>
              </div>
            )}
            {cameraErrored && !isRnWebView && (
              <div className="mb-4">
                <strong>
                  {device} has failed to load. Upload with {device} is disabled. Sorry for the inconveneince.
                </strong>
              </div>
            )}
          </div>
        </div>
      )}
      {loading && (
        <div className="d-flex justify-content-center align-items-center my-4">
          <Loading />
        </div>
      )}
      {!loading && queuedDocuments && queuedDocuments.length > 0 && (
        <div className="mt-4">
          <span style={{ fontWeight: 'bold' }}>Documents ready for submission:</span>
          <div
            style={{
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'center',
            }}
          >
            <ul
              className="mt-3"
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                width: '100%',
                maxWidth: '600px',
              }}
            >
              {queuedDocuments.map((section, index) => {
                return (
                  <div key={index}>
                    <li
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '5px',
                        marginBottom: '5px',
                        borderRadius: '5px',
                        cursor: (isUpdating || uploading) && !section.isUpdating ? 'not-allowed' : '',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          opacity: (isUpdating || uploading) && !section.isUpdating ? 0.5 : 1,
                          pointerEvents: (isUpdating || uploading) && !section.isUpdating ? 'none' : undefined,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                          }}
                        >
                          <strong
                            style={{
                              flex: 1,
                              color: 'var(--text)',
                            }}
                          >
                            {formatDocumentName(section.selectedDocument)}
                          </strong>
                          <button
                            onClick={() => {
                              handleRemoveQueuedDocument(section.selectedDocument);
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginLeft: '12px',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = 'rgba(236, 77, 92, 0.1)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            aria-label="Remove document requirement"
                          >
                            <i className="fas fa-times-circle" style={{ color: 'var(--red)', fontSize: '1.1rem' }} />
                          </button>
                        </div>
                      </div>
                    </li>
                    <li
                      key={`images-${index}`}
                      className="mb-2"
                      style={{ cursor: (isUpdating || uploading) && !section.isUpdating ? 'not-allowed' : '' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-around',
                          alignItems: 'center',
                          opacity: isUpdating && !section.isUpdating ? 0.5 : 1,
                          pointerEvents: isUpdating && !section.isUpdating ? 'none' : undefined,
                        }}
                      >
                        {section.frontFile && !section.isUpdating && (
                          <ThumbnailButtonImageViewer
                            file={section.frontFile}
                            onDelete={() => handleImageDelete('front', section.selectedDocument)}
                          />
                        )}
                        {section.frontFile === undefined && (
                          <div
                            className=" mt-2"
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              width: '100%',
                            }}
                          >
                            <div {...getFrontRootProps()}>
                              <input {...getFrontInputProps()} />
                              <Button className={'btn-btn btn-primary mr-2'} disabled={disabled}>
                                <Text id={'investigationUpload.selectUpload'} type={'field'} />
                                <Text
                                  id={'investigationUpload.upload'}
                                  type={'field'}
                                  data={{
                                    documentName: formatDocumentName(section.selectedDocument) + ' - Front',
                                  }}
                                />
                              </Button>
                            </div>
                            {cameraPermission &&
                              !cameraErrored &&
                              (cameraExists || (!cameraExists && isRnWebView)) &&
                              areDocumentsRequested && (
                                <>
                                  <input
                                    type="file"
                                    id="frontImage"
                                    ref={frontImageInput3Ref}
                                    onChange={event => handleFileUpdate(event, section)}
                                    capture="environment"
                                    accept="image/*"
                                    hidden
                                  />
                                  <button
                                    onClick={() => {
                                      if (isRnWebView)
                                        frontImageInput3Ref.current && frontImageInput3Ref.current.click();
                                      else {
                                        setOpenCameraModal(!openCameraModal);
                                        setSide('front');
                                        if (!openCameraModal && !isRnWebView) getVideo();
                                      }
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <i
                                      className="fas fa-camera"
                                      style={{ fontSize: '36px' }}
                                      title="Take photo with your camera."
                                    />
                                  </button>
                                </>
                              )}
                          </div>
                        )}
                        {section.backFile && !section.isUpdating && (
                          <ThumbnailButtonImageViewer
                            file={section.backFile}
                            onDelete={() => handleImageDelete('back', section.selectedDocument)}
                          />
                        )}
                        {section.backFile === undefined &&
                          (section.selectedDocument === InvestigationDocuments.StateIdCard ||
                            section.selectedDocument === InvestigationDocuments.DriversLicense) && (
                            <div
                              className=" mt-2"
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                              }}
                            >
                              <div {...getBackRootProps()}>
                                <input {...getBackInputProps()} />
                                <Button className={'btn-btn btn-primary mr-2'} disabled={disabled}>
                                  <Text id={'investigationUpload.selectUpload'} type={'field'} />
                                  <Text
                                    id={'investigationUpload.upload'}
                                    type={'field'}
                                    data={{
                                      documentName: formatDocumentName(section.selectedDocument) + ' - Back',
                                    }}
                                  />
                                </Button>
                              </div>
                              {cameraPermission &&
                                !cameraErrored &&
                                (cameraExists || (!cameraExists && isRnWebView)) &&
                                areDocumentsRequested && (
                                  <>
                                    <input
                                      type="file"
                                      id="frontImage"
                                      ref={backImageInput2Ref}
                                      onChange={event => handleFileUpdate(event, section)}
                                      capture="environment"
                                      accept="image/*"
                                      hidden
                                    />
                                    <button
                                      onClick={() => {
                                        if (isRnWebView)
                                          backImageInput2Ref.current && backImageInput2Ref.current.click();
                                        else {
                                          setOpenCameraModal(!openCameraModal);
                                          setSide('back');
                                          if (!openCameraModal) getVideo();
                                        }
                                      }}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <i
                                        className="fas fa-camera"
                                        style={{ fontSize: '36px' }}
                                        title="Take photo with your camera."
                                      />
                                    </button>
                                  </>
                                )}
                            </div>
                          )}
                      </div>
                    </li>
                    {section.isUpdating && errorState && <div className="error mb-4">{errorState}</div>}
                  </div>
                );
              })}
            </ul>
          </div>
          {areDocumentRequirementsFullfilled && !(isUpdating || uploading) && (
            <Button className="mt-4 mb-4" style={{ width: '100%' }} variant={'primary'} onClick={handleUploadAll}>
              SUBMIT ALL DOCUMENTS
            </Button>
          )}
        </div>
      )}
      {(cameraExists || (!cameraExists && isRnWebView)) && openCameraModal && (
        <>
          <Modal
            setShow={setOpenCameraModal}
            show={openCameraModal}
            aria-labelledby="cameraModal"
            className="service-level-modal"
          >
            <BootstrapModal.Header closeButton>
              <BootstrapModal.Title>{device} Capture</BootstrapModal.Title>
            </BootstrapModal.Header>
            <BootstrapModal.Body>
              <Formik
                initialValues={{
                  selectedDocument: requestedDocuments[0],
                }}
                as
                CameraFormValues
                onSubmit={() => {}}
              >
                {({  }: FormikProps<CameraFormValues>) => (
                  <Form translate={'yes'} id="form">
                    <div style={{ marginTop: '.5rem' }}>
                      <div className={!hasPhoto ? 'camera' : 'camera hidden'}>
                        {!cameraLoaded && (
                          <div
                            style={{
                              width: 414,
                              height: 414 / (16 / 9),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {loadingError ? <strong>{loadingError}</strong> : <Loading />}
                          </div>
                        )}
                        <video
                          ref={videoRef}
                          className="cameraCanvas"
                          onLoadedMetadata={handleLoadedMetadata}
                          style={cameraLoaded ? { display: 'block' } : { display: 'none' }}
                        ></video>
                        <canvas className="cameraCanvas" ref={photoRef} hidden></canvas>
                        {cameraLoaded && (
                          <Button variant={'info'} className="mt-2" onClick={() => takePhoto(side === 'back')}>
                            Take {isIdCard && <span>{side} </span>}Photo
                          </Button>
                        )}
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </BootstrapModal.Body>
            <BootstrapModal.Footer>
              <Button variant={'secondary'} onClick={() => setOpenCameraModal(false)}>
                Close
              </Button>
            </BootstrapModal.Footer>
          </Modal>
        </>
      )}
    </>
  );
}
